import {useQuery} from "@apollo/client";
import {Helmet} from "react-helmet-async";
import {Link, useParams, useSearchParams} from "react-router";

import Button from "@/components/button/Button.tsx";
import ContentContainer from "@/components/layout/ContentContainer.tsx";
import {ErrorFallback} from "@/lib/handlers/ErrorFallback.tsx";
import {GQL_INCIDENT, IncidentResponse} from "@/lib/queries/statuspages.ts";
import {IncidentType} from "@/lib/types/statuspages.ts";
import {IncidentForm} from "@/pages/incidents/components/incident-edit/IncidentForm.tsx";
import {NotFoundPage} from "@/pages/website/NotFoundPage.tsx";

export const EditIncidentPage = () => {
  const {id} = useParams<{id: string}>();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get("redirectTo") || "/incidents";

  const {data, loading, error} = useQuery<IncidentResponse>(GQL_INCIDENT, {
    variables: {id: parseInt(id!, 10)},
    fetchPolicy: "no-cache",
  });

  const incident = data?.incident;
  const title = incident
    ? `Edit ${incident.incident_type === IncidentType.INCIDENT ? "Incident" : "Scheduled Maintenance"}`
    : "Edit Incident";

  if (loading) {
    return (
      <ContentContainer title={title}>
        <div>Loading...</div>
      </ContentContainer>
    );
  }

  if (error) {
    return (
      <ContentContainer title={title}>
        <ErrorFallback />
      </ContentContainer>
    );
  }

  if (!incident) {
    return (
      <NotFoundPage
        title="Incident Not Found"
        message="The Incident you are looking for does not exist."
        redirectComponent={
          <Button as={Link} to="/incidents">
            Go to Incidents Page
          </Button>
        }
      />
    );
  }

  return (
    <>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <IncidentForm
        incident={incident}
        defaultIncidentType={incident.incident_type}
        redirectTo={redirectTo}
        disableStatusPageSelect={true}
      />
    </>
  );
};
